// FILE: ProviderModelPicker.tsx
// Purpose: Renders the composer provider/model menu and supports controlled opening for shortcuts.
// Layer: Chat composer presentation
// Depends on: provider availability metadata, shared menu primitives, and picker trigger styling.

import {
  type ManagedAgent,
  type ManagedAgentId,
  type ModelSlug,
  type ProviderKind,
  type ServerProviderStatus,
} from "@taskin-code/contracts";
import { resolveSelectableModel } from "@taskin-code/shared/model";
import { memo, useCallback, useState } from "react";
import { type ProviderPickerKind, PROVIDER_OPTIONS } from "../../session-logic";
import {
  Menu,
  MenuGroup,
  MenuGroupLabel,
  MenuItem,
  MenuPopup,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuSub,
  MenuSubPopup,
  MenuSubTrigger,
  MenuTrigger,
} from "../ui/menu";
import { ClaudeAI, Icon, OpenAI } from "../Icons";
import { cn } from "~/lib/utils";
import { CheckIcon } from "~/lib/icons";
import { AgentAvatar } from "../AgentAvatar";
import { PickerTriggerButton } from "./PickerTriggerButton";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { ShortcutKbd } from "../ui/shortcut-kbd";
import {
  findSelectedAgent,
  resolveLiveProviderAvailability,
  resolvePickerAgentEntries,
  resolvePickerModelProviders,
  resolvePickerTriggerLabel,
} from "./ProviderModelPicker.logic";

function isAvailableProviderOption(option: (typeof PROVIDER_OPTIONS)[number]): option is {
  value: ProviderKind;
  label: string;
  available: true;
} {
  return option.available;
}

const PROVIDER_ICON_BY_PROVIDER: Record<ProviderPickerKind, Icon> = {
  codex: OpenAI,
  claudeAgent: ClaudeAI,
};

export const AVAILABLE_PROVIDER_OPTIONS = PROVIDER_OPTIONS.filter(isAvailableProviderOption);
const UNAVAILABLE_PROVIDER_OPTIONS = PROVIDER_OPTIONS.filter((option) => !option.available);

function providerIconClassName(
  provider: ProviderKind | ProviderPickerKind,
  fallbackClassName: string,
): string {
  return provider === "claudeAgent" ? "text-foreground" : fallbackClassName;
}

export const ProviderModelPicker = memo(function ProviderModelPicker(props: {
  provider: ProviderKind;
  model: ModelSlug;
  lockedProvider: ProviderKind | null;
  providers?: ReadonlyArray<ServerProviderStatus>;
  modelOptionsByProvider: Record<ProviderKind, ReadonlyArray<{ slug: string; name: string }>>;
  /**
   * Visible managed agents (see `AgentsClient.listVisible`), offered as one flat
   * section above the models — an agent brings its own provider, so it is not
   * filed under one.
   */
  agents?: ReadonlyArray<ManagedAgent>;
  /** The agent behind the thread's current selection, if any — see `ModelSelection.agentId`. */
  selectedAgentId?: ManagedAgentId | null;
  activeProviderIconClassName?: string;
  compact?: boolean;
  disabled?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  shortcutLabel?: string | null;
  /**
   * `agentId` is present only when the selection came from picking an agent
   * — picking a plain model must call this with two arguments so a
   * previously-selected agent does not silently carry over.
   */
  onProviderModelChange: (
    provider: ProviderKind,
    model: ModelSlug,
    agentId?: ManagedAgentId,
  ) => void;
  /** Opens Settings → Providers; enables the "Sign in" entries. */
  onOpenProviderSettings?: () => void;
}) {
  const { onOpenChange, open } = props;
  const [uncontrolledMenuOpen, setUncontrolledMenuOpen] = useState(false);
  const activeProvider = props.lockedProvider ?? props.provider;
  const isMenuOpen = open ?? uncontrolledMenuOpen;
  const agents = props.agents ?? [];
  const selectedAgent = findSelectedAgent(agents, props.selectedAgentId);
  const selectedProviderOptions = props.modelOptionsByProvider[activeProvider];
  const selectedModelLabel = resolvePickerTriggerLabel(
    selectedAgent,
    selectedProviderOptions.find((option) => option.slug === props.model)?.name ?? props.model,
  );
  const ProviderIcon = PROVIDER_ICON_BY_PROVIDER[activeProvider];
  const setMenuOpen = useCallback(
    (nextOpen: boolean) => {
      if (open === undefined) {
        setUncontrolledMenuOpen(nextOpen);
      }
      onOpenChange?.(nextOpen);
    },
    [onOpenChange, open],
  );
  const handleModelChange = (provider: ProviderKind, value: string) => {
    if (props.disabled) return;
    if (!value) return;
    const resolvedModel = resolveSelectableModel(
      provider,
      value,
      props.modelOptionsByProvider[provider],
    );
    if (!resolvedModel) return;
    props.onProviderModelChange(provider, resolvedModel);
    setMenuOpen(false);
  };
  const handleAgentChange = (agent: ManagedAgent) => {
    if (props.disabled) return;
    props.onProviderModelChange(agent.provider, agent.model as ModelSlug, agent.id);
    setMenuOpen(false);
  };
  const agentEntries = resolvePickerAgentEntries({
    agents,
    providers: props.providers,
    lockedProvider: props.lockedProvider,
    selectedAgentId: props.selectedAgentId ?? null,
  });
  const modelProviderOptions = resolvePickerModelProviders({
    providerOptions: AVAILABLE_PROVIDER_OPTIONS,
    modelOptionsByProvider: props.modelOptionsByProvider,
  });
  const renderAgentSection = () => {
    if (agentEntries.length === 0) return null;
    return (
      <>
        <MenuGroup>
          <MenuGroupLabel>Agents</MenuGroupLabel>
          {agentEntries.map((entry) => {
            const AgentProviderIcon = PROVIDER_ICON_BY_PROVIDER[entry.agent.provider];
            // A disabled agent whose provider only needs a login is not a dead
            // end: the row opens the screen that performs it, mirroring how the
            // provider rows behave.
            const opensSettings = entry.disabled && entry.needsSignIn;
            return (
              <MenuItem
                key={entry.agent.id}
                disabled={entry.disabled && !entry.needsSignIn}
                onClick={
                  opensSettings
                    ? () => {
                        setMenuOpen(false);
                        props.onOpenProviderSettings?.();
                      }
                    : entry.disabled
                      ? undefined
                      : () => handleAgentChange(entry.agent)
                }
              >
                <AgentAvatar
                  agentId={entry.agent.id}
                  displayName={entry.agent.name}
                  avatarFileName={entry.agent.avatarFileName}
                  size="xs"
                  className="size-4 text-[8px] opacity-80"
                />
                <span className="truncate">{entry.agent.name}</span>
                <span className="ms-auto flex shrink-0 items-center gap-1.5">
                  {entry.availabilityLabel ? (
                    <span className="text-[11px] text-muted-foreground/80 uppercase tracking-[0.08em]">
                      {entry.availabilityLabel}
                    </span>
                  ) : (
                    // The provider is metadata here, not the way in: it tells you
                    // what the agent runs on without making you navigate by it.
                    <AgentProviderIcon
                      aria-hidden="true"
                      className={cn(
                        "size-3.5 opacity-60",
                        providerIconClassName(entry.agent.provider, "text-muted-foreground/85"),
                      )}
                    />
                  )}
                  {selectedAgent?.id === entry.agent.id ? (
                    <CheckIcon aria-hidden="true" className="size-3.5" />
                  ) : null}
                </span>
              </MenuItem>
            );
          })}
        </MenuGroup>
        <MenuSeparator />
      </>
    );
  };

  /**
   * An empty model list is a real state — an account granted an agent but no
   * models — and rendering nothing there reads as a broken menu.
   */
  const renderModelOptions = (provider: ProviderKind) => {
    const options = props.modelOptionsByProvider[provider];
    if (options.length === 0) {
      return (
        <MenuItem disabled>
          <span className="text-muted-foreground/80">No models available</span>
        </MenuItem>
      );
    }
    return (
      <MenuRadioGroup
        value={
          !selectedAgent && (props.lockedProvider !== null || props.provider === provider)
            ? props.model
            : ""
        }
        onValueChange={(value) => handleModelChange(provider, value)}
      >
        {options.map((modelOption) => (
          <MenuRadioItem
            key={`${provider}:${modelOption.slug}`}
            value={modelOption.slug}
            onClick={() => setMenuOpen(false)}
          >
            {modelOption.name}
          </MenuRadioItem>
        ))}
      </MenuRadioGroup>
    );
  };

  return (
    <Menu
      open={isMenuOpen}
      onOpenChange={(open) => {
        if (props.disabled) {
          setMenuOpen(false);
          return;
        }
        setMenuOpen(open);
      }}
    >
      {props.shortcutLabel ? (
        <Tooltip>
          <TooltipTrigger
            render={
              <MenuTrigger
                render={
                  <PickerTriggerButton
                    disabled={props.disabled ?? false}
                    compact={props.compact ?? false}
                    icon={
                      <ProviderIcon
                        aria-hidden="true"
                        className={cn(
                          "size-3.5 shrink-0",
                          providerIconClassName(activeProvider, "text-muted-foreground/70"),
                          props.activeProviderIconClassName,
                        )}
                      />
                    }
                    label={selectedModelLabel}
                  />
                }
              />
            }
          >
            <span className="sr-only">{selectedModelLabel}</span>
          </TooltipTrigger>
          {!isMenuOpen ? (
            <TooltipPopup side="top" sideOffset={6}>
              <span className="inline-flex items-center gap-2 px-1 py-0.5">
                <span>Change model</span>
                <ShortcutKbd
                  shortcutLabel={props.shortcutLabel}
                  className="h-4 min-w-4 px-1 text-[length:var(--app-font-size-ui-2xs,9px)] text-muted-foreground"
                />
              </span>
            </TooltipPopup>
          ) : null}
        </Tooltip>
      ) : (
        <MenuTrigger
          render={
            <PickerTriggerButton
              disabled={props.disabled ?? false}
              compact={props.compact ?? false}
              icon={
                <ProviderIcon
                  aria-hidden="true"
                  className={cn(
                    "size-3.5 shrink-0",
                    providerIconClassName(activeProvider, "text-muted-foreground/70"),
                    props.activeProviderIconClassName,
                  )}
                />
              }
              label={selectedModelLabel}
            />
          }
        >
          <span className="sr-only">{selectedModelLabel}</span>
        </MenuTrigger>
      )}
      <MenuPopup align="start">
        {props.lockedProvider !== null ? (
          <>
            {renderAgentSection()}
            <MenuGroup>{renderModelOptions(props.lockedProvider)}</MenuGroup>
          </>
        ) : (
          <>
            {renderAgentSection()}
            {modelProviderOptions.length === 0 && UNAVAILABLE_PROVIDER_OPTIONS.length === 0 ? (
              // Nothing to offer under Models. With an agent above, say nothing;
              // with neither, the menu would otherwise render empty.
              agentEntries.length === 0 ? (
                <MenuGroup>
                  <MenuItem disabled>
                    <span className="text-muted-foreground/80">No models available</span>
                  </MenuItem>
                </MenuGroup>
              ) : null
            ) : (
              <MenuGroup>
                {agentEntries.length > 0 ? <MenuGroupLabel>Models</MenuGroupLabel> : null}
                {modelProviderOptions.map((option) => {
                  const OptionIcon = PROVIDER_ICON_BY_PROVIDER[option.value];
                  const liveProvider = props.providers?.find(
                    (entry) => entry.provider === option.value,
                  );
                  const availability = resolveLiveProviderAvailability(liveProvider);
                  if (availability.disabled) {
                    // A provider that only needs a login is not a dead end: clicking
                    // it opens the screen that performs that login. Settings is the
                    // target rather than an inline action because signing in needs a
                    // terminal, and a draft thread has none — Settings runs it in a
                    // workspace terminal that needs no thread at all.
                    const needsSignIn = liveProvider?.authStatus === "unauthenticated";
                    return (
                      <MenuItem
                        key={option.value}
                        disabled={!needsSignIn}
                        {...(needsSignIn
                          ? {
                              onClick: () => {
                                setMenuOpen(false);
                                props.onOpenProviderSettings?.();
                              },
                            }
                          : {})}
                      >
                        <OptionIcon
                          aria-hidden="true"
                          className={cn(
                            "size-4 shrink-0 opacity-80",
                            providerIconClassName(option.value, "text-muted-foreground/85"),
                          )}
                        />
                        <span>{option.label}</span>
                        <span className="ms-auto text-[11px] text-muted-foreground/80 uppercase tracking-[0.08em]">
                          {availability.label}
                        </span>
                      </MenuItem>
                    );
                  }
                  return (
                    <MenuSub key={option.value}>
                      <MenuSubTrigger>
                        <OptionIcon
                          aria-hidden="true"
                          className={cn(
                            "size-4 shrink-0",
                            providerIconClassName(option.value, "text-muted-foreground/85"),
                          )}
                        />
                        {option.label}
                      </MenuSubTrigger>
                      <MenuSubPopup className="[--available-height:min(24rem,70vh)]">
                        <MenuGroup>{renderModelOptions(option.value)}</MenuGroup>
                      </MenuSubPopup>
                    </MenuSub>
                  );
                })}
                {UNAVAILABLE_PROVIDER_OPTIONS.length > 0 && modelProviderOptions.length > 0 && (
                  <MenuSeparator />
                )}
                {UNAVAILABLE_PROVIDER_OPTIONS.map((option) => {
                  const OptionIcon = PROVIDER_ICON_BY_PROVIDER[option.value];
                  return (
                    <MenuItem key={option.value} disabled>
                      <OptionIcon
                        aria-hidden="true"
                        className="size-4 shrink-0 text-muted-foreground/85 opacity-80"
                      />
                      <span>{option.label}</span>
                      <span className="ms-auto text-[11px] text-muted-foreground/80 uppercase tracking-[0.08em]">
                        Coming soon
                      </span>
                    </MenuItem>
                  );
                })}
              </MenuGroup>
            )}
          </>
        )}
      </MenuPopup>
    </Menu>
  );
});
